import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserProfile } from '../../services/api';
import CreatorOnboardingWizard from './CreatorOnboardingWizard';
import BusinessOnboardingWizard from './BusinessOnboardingWizard';
import './OnboardingWizard.scss';

const CompleteProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [canChangeRole, setCanChangeRole] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [error, setError] = useState('');
  const [returnTo, setReturnTo] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryReturnTo = params.get('returnTo');
    const stateReturnTo = location.state?.returnTo;
    const storedReturnTo = sessionStorage.getItem('foth_invite_return_to');
    const target = queryReturnTo || stateReturnTo || storedReturnTo || '';

    if (target?.startsWith('/')) {
      setReturnTo(target);
    }
  }, [location.search, location.state]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUserProfile();
        const userData = response.data;
        setUserType(userData.user_type);
        setCanChangeRole(userData.can_change_user_type === true);

        const baseValues = {
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          country: userData.country || '',
          city: userData.city || '',
          phone_number: userData.phone_number || '',
        };

        if (userData.user_type === 'creator') {
          setInitialValues({
            ...baseValues,
            content_categories: userData.creator_profile?.content_categories 
              ? userData.creator_profile.content_categories.map(cat => cat.id.toString())
              : [],
          });
        } else {
          setInitialValues({
            ...baseValues,
            company_name: userData.business_profile?.company_name || '',
            role: userData.business_profile?.role || '',
            ruc_tax_id: userData.business_profile?.ruc_tax_id || '',
            business_categories: userData.business_profile?.business_categories 
              ? userData.business_profile.business_categories.map(cat => cat.id.toString())
              : [],
          });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error al cargar los datos del perfil. Por favor, intenta nuevamente.');
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  if (loading) {
    return (
      <div className="onboarding-loading-screen">
        <motion.div 
          className="luxury-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        />
        <p>Preparando tu experiencia FOTH...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="complete-profile-page">
        <div className="onboarding-error">
          <p>{error}</p>
          <button className="btn-retry" onClick={() => window.location.reload()}>Reintentar</button>
        </div>
      </div>
    );
  }

  const roleLabel = userType === 'creator' ? 'Creator' : 'Brand';
  const roleSummary = userType === 'creator'
    ? 'Cuéntanos qué haces, dónde creas y cómo podemos validar tu presencia.'
    : 'Define la identidad de tu marca para conectar con creators alineados.';

  const renderWizard = () => {
    if (userType === 'creator') {
      return <CreatorOnboardingWizard initialData={initialValues} returnTo={returnTo} />;
    }

    if (userType === 'business') {
      return <BusinessOnboardingWizard initialData={initialValues} returnTo={returnTo} />;
    }

    return (
      <div className="onboarding-error">
        <p>Tipo de usuario no identificado. Por favor contacta a soporte.</p>
      </div>
    );
  };

  return (
    <div className="complete-profile-page">
      <section className="complete-profile-shell" aria-label="Completar perfil FOTH">
        <aside className="complete-profile-intro">
          <div className="complete-profile-eyebrow">Friends of the House</div>
          <h1>Completa tu perfil</h1>
          <p>{roleSummary}</p>
          <div className="complete-profile-divider" />
          <dl className="complete-profile-meta">
            <div>
              <dt>Cuenta</dt>
              <dd>{roleLabel}</dd>
            </div>
            <div>
              <dt>Tiempo</dt>
              <dd>2 min</dd>
            </div>
          </dl>
          {canChangeRole && (
            <button type="button" className="change-account-type" onClick={() => navigate('/select-role', { state: { returnTo } })}>
              {t('role_selection.change_action')}
            </button>
          )}
        </aside>

        <div className="complete-profile-panel">
          {renderWizard()}
        </div>
      </section>
    </div>
  );
};

export default CompleteProfile;
